import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { INotePreview } from "shared";
import { DateTime } from "luxon";

import { Drawer } from "../../../common/Atoms/Drawer";
import { RootState } from "../../store";
import {
  closeAndClear,
  NotesDrawerResource,
  open,
} from "../../slices/notesDrawer";
import { ContactNotes } from "../CRM/ContactPage/ContactTableSection/ContactNotes";
import { InvoiceNotes } from "../Invoice/InvoiceNotes";
import { EscalationNotes } from "../Escalations/Escalation/EscalationNotes";
import { OrganisationIdOverrideContext } from "../../hooks/useRouteOrganisationId";
import { classNames } from "../../../common/lib/classNames";

import { NotesDrawerSummary } from "./NotesDrawerSummary";

const titles: Record<NotesDrawerResource[`type`], string> = {
  contact: `Contact Notes`,
  invoice: `Invoice Notes`,
  escalation: `Escalation Notes`,
};

/** Side drawer showing the details and notes for a contact, invoice or escalation. Opened via the `notesDrawer` slice */
export function NotesDrawer() {
  const { open: isOpen, resource } = useSelector((s: RootState) => s.notesDrawer);
  const dispatch = useDispatch();

  return (
    <Drawer
      open={ isOpen }
      setOpen={ () => dispatch(closeAndClear()) }
      title={ resource ? titles[resource.type] : `Notes` }
      size={ `md` }
    >
      { resource && (
        <OrganisationIdOverrideContext.Provider value={ resource.organisationId }>
          <NotesDrawerSummary resource={ resource } />
          { resource.type === `contact` && <ContactNotes contactId={ resource.id } /> }
          { resource.type === `invoice` && <InvoiceNotes invoiceId={ resource.id } /> }
          { resource.type === `escalation` && <EscalationNotes escalationId={ resource.id } /> }
        </OrganisationIdOverrideContext.Provider>
      ) }
    </Drawer>
  );
}

/** Preview of the latest note, shown when hovering the notes icon */
function NotePreview({ note }: { note: INotePreview }) {
  const name = `${note.userFirstName || ``} ${note.userLastName || ``}`.trim();

  return (
    <div className={ `w-72` }>
      <div className={ `flex justify-between gap-2 text-xs text-gray-500` }>
        <span className={ `font-semibold text-gray-700 truncate` }>{ name || `Latest note` }</span>
        <span className={ `shrink-0` }>{ DateTime.fromISO(note.createdAt).toRelative() }</span>
      </div>
      <p className={ `mt-1.5 text-sm text-gray-800 whitespace-pre-wrap line-clamp-6` }>{ note.content }</p>
      <p className={ `mt-2 text-xs text-paidniceGreen-600` }>{ `Click to view all notes` }</p>
    </div>
  );
}

/** Table cell button that opens the notes drawer for the given resource. Pass `latestNote` to preview it on hover */
export function NotesCellButton({ resource, latestNote }: { resource: NotesDrawerResource; latestNote?: INotePreview }) {
  const dispatch = useDispatch();

  return (
    <Tooltip
      title={ latestNote ? <NotePreview note={ latestNote } /> : `View / add notes` }
      enterDelay={ latestNote ? 300 : undefined }
      slotProps={ latestNote ? {
        tooltip: { className: `!bg-white !text-gray-800 !max-w-none !p-3 border border-gray-200 !rounded-md !shadow-lg` },
      } : undefined }
    >
      <button
        type={ `button` }
        className={ classNames(
          `w-full h-full flex items-center justify-center hover:text-paidniceGreen-600`,
          latestNote ? `text-blue-600` : `text-gray-400`,
        ) }
        onClick={ e => {
          e.stopPropagation();
          dispatch(open(resource));
        } }
      >
        <span className={ `sr-only` }>{ `View / add notes` }</span>
        <ChatBubbleLeftEllipsisIcon className={ `h-5 w-5` } />
      </button>
    </Tooltip>
  );
}
